import {useState, useEffect} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import SwitchSelector from 'react-switch-selector';
import { NotificationManager } from 'react-notifications';
import { fileToBase64 } from '../../../../utils/fileToBase64';
import Input from '../../../common/simple/input';
import Button from '../../../common/Button';
import { newGraySelectStyles } from '../../../common/shared/select.styled';
import ServerData from "../../../common/serverData";
import { constants } from '../../../common/constants/constants';
import { hideLoader, showLoader } from '../../../store/common/slice';
import upload from '../../../images/newDesign/upload.svg';
import cross from '../../../images/newDesign/cross.svg';
import btnArrow from '../../../images/newDesign/arrow_in_white_circle.svg';
import arrow from '../../../images/newDesign/arrow.svg';
import Pdf from '../../../images/pdf.svg';
import { MaintenanceContainerStyled } from './TenantMaintenance.styled';
import PaymentFlowContainer from '../../../tenants/buildingPayment/PaymentFlowContainer';
import * as Sentry from "@sentry/react";

const TenantMaintenance = ({ match, address }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { buildingID } = match.params;
    const history = useHistory();

    const client = new ServerData();

    const ltr = useSelector(state => state.common.ltrEnabled);
    const apartments = useSelector(state => state.buildings.currentBuildingData?.apartments ?? []);

    const [inputValues, setInputValues] = useState({
        name: '',
        phone: '',
        email: '',
        desc: ''
    });
    const [selectedApt, setSelectedApt] = useState({
        label: '',
        value: ''
    });
    const [selectedCategory, setSelectedCategory] = useState({
        label: '',
        value: ''
    });
    const [currentUrgency, setCurrentUrgency] = useState('medium'); // NOTE: check also initialSelectedIndex of selector
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedCategoryOptions, setSelectedCategoryOptions] = useState([]);
    const [sendBtnDisabled, setSendBtnDisabled] = useState(true);

    useEffect(async () => {
        dispatch(showLoader());
        try {
            // NOTE: buildingID may be string or number, server will replace string value by internal ID
            const categories = await client.managers.maintenance.getFaultCategories(buildingID);
            const options = categories.filter(c => !c.IsDeleted).map(c => ({ label: c.Name, value: c.Id }));
            setSelectedCategoryOptions(options);
        } catch (e) {
            console.log(e);
        } finally {
            dispatch(hideLoader());
        }
    }, [buildingID]);

    const onUpdateInputValues = (e, type) => {
        setInputValues({
            ...inputValues,
            [type]: e.target.value
        })
    };

    const onApartmentChanged = apt => setSelectedApt(apt);
    const onCategoryChanged = cat => setSelectedCategory(cat);

    const onUploadFiles = async (e) => {
        const file = e.target.files[0];
        const base64 = await fileToBase64(file);
        let url;
        if (file.type.includes('image/')) url = URL.createObjectURL(file); // FIXME: object should be cleaned on component destroy
        else if (file.type.includes('application/pdf')) url=Pdf
        setUploadedFiles([...uploadedFiles, {name: file.name, url, base64}]);
    };

    const onImageDelete = i => {
        let images = uploadedFiles.slice();
        images.splice(i, 1);
        setUploadedFiles(images);
    };

    async function handleSendClick() {
        const CategoryId = selectedCategory.value;
        const Comments = inputValues.desc;
        const Sevirity = currentUrgency;

        const name = (inputValues.name ?? '').trim();
        const phone = (inputValues.phone ?? '').trim();
        const email = (inputValues.email ?? '').trim();
        const OpeningResident = {
            Name: name === '' ? null : name,
            Phone: phone === '' ? null : phone,
            Email: email === '' ? null : email,
        };

        const AppImages = uploadedFiles.map(f => f.base64);

        const ExtraFields = { TenantName: name };

        const { apartmentNumber } = selectedApt;

        try {
            dispatch(showLoader());
            const result = await client.managers.maintenance.createFault(buildingID, apartmentNumber, { Sevirity, Comments, CategoryId, OpeningResident, AppImages, ExtraFields });
            console.log(result);
            history.push({pathname: `/${constants.pages.confirmationPage}/${buildingID}`});
        } catch (e) {
            NotificationManager.error(t('maintenance.fault_creation_failed'));
            console.error(e);
            Sentry.captureException(e);
        } finally {
            dispatch(hideLoader());
        }
    }

    useEffect(() => {
        if (!inputValues.desc ||
            !inputValues.name ||
            !inputValues.email ||
            !inputValues.phone ||
            !selectedCategory.value ||
            !selectedApt.value) setSendBtnDisabled(true)
        else setSendBtnDisabled(false)
    }, [inputValues, selectedCategory, selectedApt]);

    const onBackBtnClick = () => window.history.back();

    return (
        <PaymentFlowContainer
            title={t('maintenance.open_a_call')}
            subheader={t('maintenance.continue_to_choose')}
            address={address}
            buildingID={buildingID}
        >
            <div className='back-btn' onClick={onBackBtnClick}><img src={arrow} />{t('auto_charge.back')}</div>

            <MaintenanceContainerStyled ltr={ltr}>
                <div className='top-container'>
                    <div>
                        <label className="label">{t('maintenance.apt')}</label>
                        <Select
                            className='select'
                            styles={newGraySelectStyles}
                            options={apartments}
                            value={selectedApt}
                            onChange={onApartmentChanged}
                            isRtl={!ltr}
                            isSearchable={false}
                        />
                    </div>

                    <Input
                        label={t('maintenance.name')}
                        name="name"
                        id="name"
                        value={inputValues.name}
                        onChange={e => onUpdateInputValues(e, 'name')}
                    />
                    <Input
                        label={`${t('notifications.phone')} *`}
                        name="phone"
                        id="phone"
                        value={inputValues.phone}
                        onChange={e => onUpdateInputValues(e, 'phone')}
                    />
                    <Input
                        label={t('maintenance.email')}
                        name="email"
                        id="email"
                        value={inputValues.email}
                        onChange={e => onUpdateInputValues(e, 'email')}
                    />
                </div>

                <div className='call-details'>{t('maintenance.call_details')}</div>

                <div className='middle-container'>
                    <div>
                        <label className="label">{t('maintenance.category')}</label>
                        <Select
                            className='select'
                            styles={newGraySelectStyles}
                            options={selectedCategoryOptions}
                            value={selectedCategory}
                            onChange={onCategoryChanged}
                            isRtl={!ltr}
                            isSearchable={false}
                        />
                    </div>
                    <div>
                        <label className="label">{t('maintenance.urgency')}</label>
                        <SwitchSelector
                            initialSelectedIndex={1}
                            options={[
                                {
                                    label: t('maintenance.low'),
                                    value: 'low',
                                    selectedBackgroundColor: 'linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)',
                                },
                                {
                                    label: t('maintenance.medium'),
                                    value: 'medium',
                                    selectedBackgroundColor: 'linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)',
                                },
                                {
                                    label: t('maintenance.high'),
                                    value: 'high',
                                    selectedBackgroundColor: 'linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)',
                                }
                            ]}
                            selectedFontColor='#FFFFFF'
                            fontColor='#373647'
                            backgroundColor='#F3F1FF'
                            onChange={e => setCurrentUrgency(e)}
                        />
                    </div>
                </div>

                <div className='bottom-container'>
                    <div className='text-area'>
                        <label className="label" for="desc">{t('maintenance.desc')}</label>
                        <textarea
                            id="desc"
                            name="desc"
                            rows="5"
                            value={inputValues.desc}
                            onChange={e => onUpdateInputValues(e, 'desc')}
                        />
                    </div>

                    <label className='upload-picture' htmlFor='upload-files'>
                        <input id='upload-files' type="file" onChange={onUploadFiles} />
                        <div>{t('maintenance.attach_a_photo')}</div>
                        <img src={upload} className='upload' />
                    </label>
                </div>

                {uploadedFiles.length > 0 && <div className='uploaded-files'>
                    {uploadedFiles.map((file, i) => {
                        return (
                            <div key={i}>
                                <div className='uploaded-image' onClick={() => onImageDelete(i)}>
                                    <img src={file.url} className='image'/>
                                    <div className='overlay'><img src={cross}/></div>
                                </div>
                                <div className='file-name'>{file.name}</div>
                            </div>
                        )
                    })}
                </div>}

                <Button onClick={handleSendClick} disabled={sendBtnDisabled}>
                    {t('maintenance.send')}
                    <img src={btnArrow} className="arrow" />
                </Button>
            </MaintenanceContainerStyled>
        </PaymentFlowContainer>
    )
}

export default TenantMaintenance;
